/**
 * İmza tableti bağdaştırıcısı.
 *
 * Yüz yüze kanallarda müşteri imzasını fiziksel bir tablete atıyor. Topaz
 * tabletler tarayıcıya doğrudan bağlanmıyor: bilgisayara kurulan SigWeb
 * servisi tableti dinliyor, sayfadaki SigWebTablet.js de o servisle yerel
 * bir bağlantı üzerinden konuşuyor.
 *
 * İki kaynak var, hangisinin kullanılacağı çağrıda belirtiliyor:
 *   'real' : gerçek cihaz -> /SigWebTablet.js       (Topaz'ın dosyası)
 *   'mock' : simülatör    -> /SigWebTablet.mock.js  (ekrana fareyle çizilir)
 *
 * Simülatör sekmesi yalnızca test ortamında görünüyor; ortamı sunucu
 * bildiriyor, arayüz kendi başına karar vermiyor.
 *
 * Kurulum (gerçek cihaz için, tablet bağlı her bilgisayarda bir kez):
 *   1. Topaz sitesinden SigWeb'i indirip kur
 *   2. SigWebTablet.js dosyasını frontend'in public/ klasörüne koy
 */

const SOURCES = {
  real: '/dist/SigWebTablet.js',
  mock: '/dist/SigWebTablet.mock.js'
};

// Iki betik de aynı global fonksiyonları tanımlıyor, bu yüzden aynı anda
// yalnızca biri yüklü olabiliyor. Sekme değişince diğeri yükleniyor.
let loadedKind = null;
let loading = null;

function loadScript(kind) {
  if (typeof window === 'undefined') return Promise.reject(new Error('no-window'));
  if (loadedKind === kind && typeof window.SetTabletState === 'function') {
    return Promise.resolve();
  }
  if (loading && loading.kind === kind) return loading.promise;

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SOURCES[kind] || SOURCES.real;
    script.onload = () => { loadedKind = kind; resolve(); };
    script.onerror = () => { loading = null; reject(new Error('script')); };
    document.head.appendChild(script);
  });

  loading = { kind: kind, promise: promise };
  return promise;
}

// Tabletten ne geldiğini tarayıcıda görebilmek için konsol çıktısı
function log(title, data) {
  try {
    /* eslint-disable no-console */
    console.groupCollapsed('%c[imza tableti] ' + title, 'color:#0a7563; font-weight:700');
    Object.keys(data).forEach((k) => console.log(k + ':', data[k]));
    console.groupEnd();
    /* eslint-enable no-console */
  } catch (e) { /* konsol yoksa sorun değil */ }
}

function isSimulator() {
  return typeof window !== 'undefined' && !!window.__SIGWEB_SIMULATOR__;
}

// SigWeb fonksiyonları global; tek tek var mı diye bakıyoruz ki eksik
// veya farklı sürümlü bir betik sayfayı çökertmesin
function has(name) {
  return typeof window[name] === 'function';
}

/**
 * Tablet kullanılabilir mi.
 * Sonuç: { ok: true } veya { ok: false, reason: 'script' | 'service' }
 *   script  : SigWebTablet.js sunucuda yok
 *   service : SigWeb bu bilgisayarda kurulu değil ya da çalışmıyor
 */
function detect(kind) {
  return loadScript(kind)
    .then(() => {
      if (!has('SetTabletState') || !has('GetSigImageB64')) {
        return { ok: false, reason: 'script' };
      }
      // Servisin gercekten ayakta oldugunu SINA.
      // IsSigWebInstalled bazi surumlerde servis kapaliyken de true
      // donuyor; o zaman kullanici imza almaya kadar geliyor ve konsolda
      // ERR_CONNECTION_REFUSED goruyor. Burada servise gercek bir soru
      // sorup cevap alamazsak kullaniciyi en basta uyariyoruz.
      try {
        if (has('IsSigWebInstalled') && !window.IsSigWebInstalled()) {
          return { ok: false, reason: 'service' };
        }

        let probe = null;
        if (has('GetSigWebVersion')) probe = window.GetSigWebVersion();
        else if (has('GetTabletState')) probe = window.GetTabletState();

        // Servis kapaliyken cagri ya hata firlatiyor ya da bos donuyor
        if (probe === undefined || probe === null || probe === '') {
          return { ok: false, reason: 'service' };
        }
      } catch (e) {
        return { ok: false, reason: 'service' };
      }

      log('bağlandı', {
        kaynak: kind === 'mock' ? 'simülatör' : 'gerçek cihaz',
        betik: SOURCES[kind],
        sürüm: has('GetSigWebVersion') ? window.GetSigWebVersion() : '(bilinmiyor)',
        cihaz: deviceInfo(),
        tabletDurumu: has('GetTabletState') ? window.GetTabletState() : '(okunamadı)'
      });
      return { ok: true, kind: kind };
    })
    .catch((err) => ({
      ok: false,
      reason: err && err.message === 'script' ? 'script' : 'service'
    }));
}

let timer = null;

/**
 * Tableti dinlemeye başlar; imza atıldıkça verilen tuvale çizilir.
 */
function start(canvas) {
  if (!has('SetTabletState') || !canvas) return;
  stop();
  const ctx = canvas.getContext('2d');
  if (has('SetDisplayXSize')) window.SetDisplayXSize(canvas.width);
  if (has('SetDisplayYSize')) window.SetDisplayYSize(canvas.height);
  if (has('SetJustifyMode')) window.SetJustifyMode(0);
  if (has('ClearTablet')) window.ClearTablet();
  // Son parametre yenileme aralığı (ms): tuval bu sıklıkla güncelleniyor
  timer = window.SetTabletState(1, ctx, 50);
}

function clear() {
  if (has('ClearTablet')) window.ClearTablet();
}

function stop() {
  if (has('SetTabletState')) {
    try { window.SetTabletState(0, timer); } catch (e) { /* zaten kapalı */ }
  }
  timer = null;
}

// Tablette mürekkep var mı
function hasInk() {
  try {
    return has('NumberOfTabletPoints') && window.NumberOfTabletPoints() > 0;
  } catch (e) {
    return false;
  }
}

/**
 * İmzayı görsel olarak alır.
 * SigWeb'in ürettiği PNG'nin biçimi (bit derinliği, palet) sürüme göre
 * değişiyor; sunucu yalnızca 8 bitlik RGBA PNG kabul ettiği için sonucu
 * bir tuvale çizip yeniden dışa aktarıyoruz.
 */
function capture() {
  return new Promise((resolve, reject) => {
    if (!hasInk()) {
      reject(new Error('empty'));
      return;
    }
    try {
      window.SetSigCompressionMode(1);
      window.SetImageXSize(600);
      window.SetImageYSize(180);
      window.SetImagePenWidth(5);
      window.SetJustifyMode(5);          // imzayı görselin içinde ortala

      const started = Date.now();

      window.GetSigImageB64((base64) => {
        if (!base64) {
          reject(new Error('empty'));
          return;
        }
        const img = new Image();
        img.onload = () => {
          const out = document.createElement('canvas');
          out.width = img.width;
          out.height = img.height;
          out.getContext('2d').drawImage(img, 0, 0);
          const image = out.toDataURL('image/png');

          // Topaz'ın biyometrik imza dizisi: basınç ve zamanlama taşıyor.
          // Okuyup gösteriyoruz ama SUNUCUYA GÖNDERMİYORUZ; biyometrik
          // veri saklamak ayrı bir karar.
          let sigString = '';
          let sigStats = null;
          try {
            if (has('GetSigString')) sigString = String(window.GetSigString() || '');
          } catch (e) { /* servis kapalıysa okunamıyor */ }
          try {
            if (has('GetSigStats')) sigStats = window.GetSigStats();
          } catch (e) { /* gerçek cihazda bu fonksiyon yok */ }

          const result = {
            image: image,
            device: deviceInfo(),
            simulator: isSimulator(),
            points: has('NumberOfTabletPoints') ? window.NumberOfTabletPoints() : 0,
            width: img.width,
            height: img.height,
            sigString: sigString,
            sigStats: sigStats
          };

          log('imza alındı', {
            kaynak: result.simulator ? 'simülatör' : 'gerçek cihaz',
            cihaz: result.device,
            noktaSayısı: result.points,
            görselÖlçüsü: result.width + ' x ' + result.height,
            görselBoyutu: Math.round(image.length * 0.75 / 1024) + ' KB (yaklaşık)',
            süre: (Date.now() - started) + ' ms',
            // Ham dizi konsola YAZILMIYOR: imza cihazi genelde ortak
            // kullanilan bir makine, ekranini baskasi gorebilir.
            // Yalnizca var mi ve ne kadar oldugu.
            biyometrikDizi: sigString
              ? '(' + sigString.length + ' karakter, konsola yazilmiyor)'
              : '(okunamadı — servis kapalı olabilir)',
            biyometrikÖzet: sigStats || '(cihaz özet vermiyor)',
            sunucuyaGidecek: {
              signatureType: 'pad',
              signatureDevice: result.device,
              signatureImage: image.slice(0, 48) + '…'
            },
            önizleme: image
          });

          resolve(result);
        };
        img.onerror = () => reject(new Error('image'));
        img.src = 'data:image/png;base64,' + base64;
      });
    } catch (e) {
      reject(e);
    }
  });
}

// Tablet modelini ve seri numarasını okur; denetim izine yazılıyor.
// SigWeb bu bilgiyi her sürümde vermiyor, yoksa yalnızca marka kalıyor.
function deviceInfo() {
  // Simülatörle atılan imza denetim izinde ayırt edilebilsin
  const parts = [isSimulator() ? 'SIMULATOR' : 'Topaz'];
  try {
    if (has('TabletModelNumber')) parts.push(String(window.TabletModelNumber()));
    if (has('TabletSerialNumber')) parts.push('SN ' + String(window.TabletSerialNumber()));
  } catch (e) { /* bilgi alınamadı */ }
  return parts.join(' ');
}

export default {
  detect, start, clear, stop, hasInk, capture, deviceInfo, isSimulator, SOURCES
};


// WEBPACK FOOTER //
// ./src/config/signaturePad.js